/**
 * Semantic status-tone convention ported from the omecare-mockups design.
 *
 * Domain code maps its statuses onto one of these named tones instead of
 * hand-picking Tailwind palette classes. Semantics used across the app:
 * emerald = validated/active, amber = pending/reserved, sky = open/evening,
 * rose = refused/to-sign/sick, violet = attending/full-day,
 * orange = morning, yellow = paid leave.
 */
export type Tone = {
  /** Solid dot color (legend dots, toggle-filter dots). */
  dot: string;
  /** Soft pill background for badges and chips. */
  pill: string;
  /** Left-border accent for slot chips. */
  border: string;
  /** Full, softened border for a tinted surface (tiles, selected options). */
  surfaceBorder: string;
  /** Foreground text color, light + dark variants. */
  text: string;
};

export const TONES = {
  orange: {
    dot: 'bg-orange-500',
    pill: 'bg-orange-500/10',
    border: 'border-l-orange-500',
    surfaceBorder: 'border-orange-500/30',
    text: 'text-orange-700 dark:text-orange-300',
  },
  yellow: {
    dot: 'bg-yellow-500',
    pill: 'bg-yellow-500/10',
    border: 'border-l-yellow-500',
    surfaceBorder: 'border-yellow-500/30',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  emerald: {
    dot: 'bg-emerald-500',
    pill: 'bg-emerald-500/10',
    border: 'border-l-emerald-500',
    surfaceBorder: 'border-emerald-500/30',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  amber: {
    dot: 'bg-amber-500',
    pill: 'bg-amber-500/10',
    border: 'border-l-amber-500',
    surfaceBorder: 'border-amber-500/30',
    text: 'text-amber-700 dark:text-amber-300',
  },
  sky: {
    dot: 'bg-sky-500',
    pill: 'bg-sky-500/10',
    border: 'border-l-sky-500',
    surfaceBorder: 'border-sky-500/30',
    text: 'text-sky-700 dark:text-sky-300',
  },
  rose: {
    dot: 'bg-rose-500',
    pill: 'bg-rose-500/10',
    border: 'border-l-rose-500',
    surfaceBorder: 'border-rose-500/30',
    text: 'text-rose-700 dark:text-rose-300',
  },
  violet: {
    dot: 'bg-violet-500',
    pill: 'bg-violet-500/10',
    border: 'border-l-violet-500',
    surfaceBorder: 'border-violet-500/30',
    text: 'text-violet-700 dark:text-violet-300',
  },
  fuchsia: {
    dot: 'bg-fuchsia-500',
    pill: 'bg-fuchsia-500/10',
    border: 'border-l-fuchsia-500',
    surfaceBorder: 'border-fuchsia-500/30',
    text: 'text-fuchsia-700 dark:text-fuchsia-300',
  },
  teal: {
    dot: 'bg-teal-500',
    pill: 'bg-teal-500/10',
    border: 'border-l-teal-500',
    surfaceBorder: 'border-teal-500/30',
    text: 'text-teal-700 dark:text-teal-300',
  },
  slate: {
    dot: 'bg-slate-500',
    pill: 'bg-slate-500/10',
    border: 'border-l-slate-500',
    surfaceBorder: 'border-slate-500/30',
    text: 'text-slate-700 dark:text-slate-300',
  },
} as const satisfies Record<string, Tone>;

export type ToneName = keyof typeof TONES;

/**
 * Badge className for an active status: soft pill + tinted text, borderless.
 *
 * @param tone - the semantic tone to render
 * @returns Tailwind classes to merge into a `Badge variant="outline"`
 */
export const toneBadgeClassName = (tone: Tone): string =>
  `${tone.pill} ${tone.text} border-transparent`;

/** Badge className for inactive/expired/negative states (mockup convention). */
export const MUTED_BADGE_CLASSNAME = 'border-dashed text-muted-foreground';

/**
 * Fixed decorative gold used by the "installable substitute" star. Deliberately
 * not a tone: it reads as a gold medal, identical in light and dark.
 */
export const INSTALLABLE_STAR_CLASSNAME = 'fill-yellow-400 text-yellow-500';
